if (!customElements.get('card-variant-quick-add')) {
  customElements.define(
    'card-variant-quick-add',
    class CardVariantQuickAdd extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.form = this.closest('form');
        if (!this.form) return;

        this.variantSelect = this.querySelector('[data-card-variant-select]');
        this.variantPills = Array.from(this.querySelectorAll('[data-card-variant-pill]'));
        this.variantIdInput = this.form.querySelector('input.product-variant-id, input[name="id"]');
        this.submitButton = this.form.querySelector('[data-card-variant-submit-button]');
        this.buttonText = this.form.querySelector('[data-card-variant-button-text]');
        this.productTitle = this.dataset.productTitle || '';
        this.addToCartLabel = this.buttonText?.dataset.addToCartLabel || this.buttonText?.textContent?.trim() || 'Add to cart';
        this.soldOutLabel = this.submitButton?.querySelector('.sold-out-message')?.textContent?.trim() || 'Sold out';
        this.priceBlock =
          this.closest('.card__content')?.querySelector('[data-card-variant-price]') ||
          this.closest('.card-wrapper')?.querySelector('[data-card-variant-price]');
        this.moneyFormat = this.priceBlock?.dataset.moneyFormat || window.Shopify?.money_format || '${{amount}}';
        this.onVariantChange = this.onVariantChange.bind(this);
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        if (this.variantSelect) {
          this.variantSelect.addEventListener('change', this.onVariantChange, { signal });
        }

        this.variantPills.forEach((pill) => {
          pill.addEventListener('change', this.onVariantChange, { signal });
        });

        this.initialized = true;
        this.syncSelectedVariant({ updatePrice: false });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.initialized = false;
      }

      onVariantChange() {
        this.syncSelectedVariant({ updatePrice: true });
      }

      getSelectedOption() {
        if (this.variantSelect) {
          return this.variantSelect.selectedOptions[0] || null;
        }

        return this.querySelector('[data-card-variant-pill]:checked');
      }

      syncSelectedVariant({ updatePrice = true } = {}) {
        const selectedOption = this.getSelectedOption();
        if (!selectedOption || !this.variantIdInput || !this.submitButton) return;

        const variantId = selectedOption.value;
        if (!variantId) return;

        const isAvailable = selectedOption.dataset.available === 'true';
        const variantTitle = (
          selectedOption.matches?.('[data-card-variant-pill]')
            ? this.querySelector(`label[for="${selectedOption.id}"]`)?.textContent
            : selectedOption.textContent
        )
          ?.replace(/\s+/g, ' ')
          .trim();

        this.variantIdInput.value = variantId;
        // product-form enables this input on upgrade; keep sold-out variants out of FormData.
        this.variantIdInput.disabled = !isAvailable;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));

        this.submitButton.disabled = !isAvailable;
        this.submitButton.toggleAttribute('disabled', !isAvailable);

        if (this.buttonText) {
          this.buttonText.textContent = isAvailable ? this.addToCartLabel : this.soldOutLabel;
          this.buttonText.classList.toggle('hidden', false);
        }

        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
        }

        if (isAvailable) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.setAttribute(
            'aria-label',
            `Add ${this.productTitle}${variantTitle ? ` - ${variantTitle}` : ''} to your cart`
          );
        } else {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute(
            'aria-label',
            variantTitle ? `${variantTitle} is sold out` : `${this.productTitle} is sold out`
          );
        }

        if (updatePrice) {
          this.updatePrice(selectedOption);
        }
      }

      updatePrice(selectedOption) {
        if (!this.priceBlock || !selectedOption) return;

        const price = Number(selectedOption.dataset.price || 0);
        const compareAtPrice = Number(selectedOption.dataset.compareAtPrice || 0);
        if (!Number.isFinite(price)) return;

        const onSale = Number.isFinite(compareAtPrice) && compareAtPrice > price;
        const formattedPrice = this.formatMoney(price);
        const formattedCompareAt = this.formatMoney(compareAtPrice);
        const priceElement = this.priceBlock.querySelector('.price') || this.priceBlock;
        const promoPercent = Number(priceElement.dataset.promoPercent || 0);

        if (promoPercent > 0) {
          const salePrice = price - Math.floor((price * promoPercent) / 100);
          const formattedSale = this.formatMoney(salePrice);
          const saleNode = priceElement.querySelector('[data-promo-sale-price]');
          const compareNode = priceElement.querySelector('[data-promo-compare-price]');
          if (saleNode) {
            saleNode.textContent = formattedSale;
            saleNode.setAttribute('aria-label', formattedSale);
          }
          if (compareNode) compareNode.textContent = formattedPrice;
          return;
        }

        priceElement.classList.toggle('price--on-sale', onSale);

        const regularPrice = priceElement.querySelector('.price__regular .price-item--regular');
        if (regularPrice) {
          const strong = regularPrice.querySelector('strong');
          if (strong) {
            strong.textContent = formattedPrice;
            strong.setAttribute('aria-label', formattedPrice);
          } else {
            regularPrice.textContent = formattedPrice;
          }
        }

        const salePrice = priceElement.querySelector('.price__sale .price-item--sale');
        if (salePrice) {
          salePrice.textContent = formattedPrice;
        }

        const comparePrice = priceElement.querySelector('.price__sale s.price-item--regular, .price__sale .price-item--regular');
        if (comparePrice) {
          comparePrice.textContent = formattedCompareAt;
        }
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(cents, this.moneyFormat);
        }

        const placeholderMatch = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
        const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
        const number = Number(cents) || 0;

        const value = (() => {
          switch (placeholder) {
            case 'amount_no_decimals':
              return (number / 100).toFixed(0);
            case 'amount_with_comma_separator':
              return (number / 100)
                .toFixed(2)
                .replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            case 'amount_no_decimals_with_comma_separator':
              return (number / 100)
                .toFixed(0)
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            case 'amount_with_apostrophe_separator':
              return (number / 100)
                .toFixed(2)
                .replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, "'");
            default:
              return (number / 100).toFixed(2);
          }
        })();

        return placeholderMatch ? this.moneyFormat.replace(placeholderMatch[0], value) : value;
      }
    }
  );
}
